// Registo de um estafeta pela Pagali (Admin): conta + ficha já APROVADA.
// Mesmos campos do registo público (pagali.cv/driver/register); a password é entregue ao estafeta.
import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid2 as Grid, MenuItem, TextField, Typography,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import driversService from '../../../services/drivers.service';

const VEICULOS = [
  { key: 'MOTO', label: 'Moto' },
  { key: 'CARRO', label: 'Carro' },
  { key: 'BICICLETA', label: 'Bicicleta' },
  { key: 'A_PE', label: 'A pé' },
];

const vazio = {
  name: '', email: '', phone: '', password: '',
  biNumero: '', cartaConducao: '', vehicleType: 'MOTO', vehiclePlate: '', vehicleModel: '',
};

interface Props {
  open: boolean;
  onClose: () => void;
  onCriado: (msg: string) => void;
}

const NovoEstafetaDialog: React.FC<Props> = ({ open, onClose, onCriado }) => {
  const [form, setForm] = useState(vazio);
  const [docs, setDocs] = useState<{ biDoc?: File; cartaDoc?: File; avatar?: File }>({});
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const set = (k: keyof typeof vazio) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const fechar = () => { setForm(vazio); setDocs({}); setErro(null); onClose(); };

  const gravar = async () => {
    setErro(null);
    const precisaCarta = ['MOTO', 'CARRO'].includes(form.vehicleType);
    if (!form.name || !form.email || !form.phone || form.password.length < 6 || !form.biNumero || (precisaCarta && !form.cartaConducao)) {
      setErro(`Preencha nome, email, telefone, password (mín. 6), BI/CNI${precisaCarta ? ' e carta de condução' : ''}.`);
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (docs.biDoc) fd.append('biDoc', docs.biDoc);
      if (docs.cartaDoc) fd.append('cartaDoc', docs.cartaDoc);
      if (docs.avatar) fd.append('avatar', docs.avatar);
      const r = await driversService.create(fd);
      onCriado(r.data?.data?.convertida
        ? `${form.email} já tinha conta Pagali: foi convertida em estafeta e aprovada. Entra com a password que já usava.`
        : `${form.name} registado e aprovado. Entregue-lhe o email e a password para entrar na app.`);
      fechar();
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Não foi possível registar o estafeta');
    }
    setSaving(false);
  };

  const ficheiro = (k: 'biDoc' | 'cartaDoc' | 'avatar', label: string) => (
    <Button component="label" variant="outlined" size="small" startIcon={<UploadFile />} sx={{ justifyContent: 'flex-start' }} fullWidth>
      {docs[k] ? docs[k]!.name : label}
      <input hidden type="file" accept="image/*,application/pdf"
        onChange={(e) => setDocs((d) => ({ ...d, [k]: e.target.files?.[0] }))} />
    </Button>
  );

  return (
    <Dialog open={open} onClose={fechar} maxWidth="sm" fullWidth>
      <DialogTitle>Novo estafeta</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          O estafeta fica aprovado de imediato e entra na app Pagali com este email e password. Se o
          email já tiver conta de cliente, essa conta passa a estafeta e mantém a password que tinha.
        </Typography>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}><TextField fullWidth label="Nome completo" value={form.name} onChange={set('name')} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Email" type="email" value={form.email} onChange={set('email')} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="Telefone" placeholder="+238 991 23 45" value={form.phone} onChange={set('phone')} /></Grid>
          <Grid size={{ xs: 12 }}>
            <TextField fullWidth label="Password inicial" value={form.password} onChange={set('password')}
              helperText="Mínimo 6 caracteres. Entregue-a ao estafeta; ele pode mudá-la na app." />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label="BI / CNI" value={form.biNumero} onChange={set('biNumero')} /></Grid>
          <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth label={['MOTO', 'CARRO'].includes(form.vehicleType) ? 'Carta de condução' : 'Carta de condução (opcional)'} value={form.cartaConducao} onChange={set('cartaConducao')} /></Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth select label="Veículo" value={form.vehicleType} onChange={set('vehicleType')}>
              {VEICULOS.map((v) => <MenuItem key={v.key} value={v.key}>{v.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label="Matrícula" value={form.vehiclePlate} onChange={set('vehiclePlate')} /></Grid>
          <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth label="Modelo" value={form.vehicleModel} onChange={set('vehicleModel')} /></Grid>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
              {ficheiro('biDoc', 'Foto do BI/CNI')}
              {ficheiro('cartaDoc', 'Foto da carta')}
              {ficheiro('avatar', 'Fotografia')}
            </Box>
            <Typography variant="caption" color="text.secondary">Opcional — os documentos ficam privados.</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={fechar}>Cancelar</Button>
        <Button variant="contained" onClick={gravar} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Registar e aprovar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NovoEstafetaDialog;
